import { useState, useEffect, useCallback, type FC, type ReactNode } from 'react';
import auth from './auth-service';
import { AuthContext } from './auth-context';
import { ApiError } from './http';
import { type User } from '../types';

interface AuthProviderProps {
	children: ReactNode;
}

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
	// A remembered account lets signed-in visitors see the app straight away
	// while /auth/me revalidates it in the background (stale-while-revalidate).
	const [initialUser] = useState<User | null>(() => auth.cachedUser() ?? null);
	const [isLoggedin, setIsLoggedin] = useState(initialUser !== null);
	const [user, setUser] = useState<User | null>(initialUser);
	// Only block on the session check when there is a session to check. Deriving
	// this instead of setting it from the effect avoids a cascading render, and
	// means signed-out visitors no longer flash "Loading" before the first paint.
	const [isLoading, setIsLoading] = useState(() => auth.hasSession() && initialUser === null);
	const [authError, setAuthError] = useState<string | null>(null);

	useEffect(() => {
		if (!auth.hasSession()) return;
		auth
			.me()
			.then((user: User) => {
				setIsLoggedin(true);
				setUser(user);
			})
			.catch(() => {
				setIsLoggedin(false);
				setUser(null);
			})
			.finally(() => setIsLoading(false));
	}, []);

	const applyUser = (user: User): void => {
		setIsLoggedin(true);
		setUser(user);
		setAuthError(null);
	};

	const handleAuthError = (error: unknown): void => {
		if (error instanceof ApiError) {
			setAuthError(error.message);
		} else if (error instanceof TypeError) {
			// fetch rejects with a TypeError when the request never gets a usable
			// response: offline, DNS, or the API refusing this origin via CORS
			setAuthError('Could not reach the server — please check your connection and try again');
		} else {
			setAuthError('Something went wrong — please try again');
		}
	};

	const signup = (credentials: { username: string; password: string }): void => {
		setAuthError(null);
		auth.signup(credentials).then(applyUser).catch(handleAuthError);
	};

	const login = (credentials: { username: string; password: string }): void => {
		setAuthError(null);
		auth.login(credentials).then(applyUser).catch(handleAuthError);
	};

	const logout = (): void => {
		auth
			.logout()
			.then(() => {
				setIsLoggedin(false);
				setUser(null);
			})
			.catch(() => {});
	};

	// Re-fetch the account (e.g. after a plan change) so every consumer sees it
	const refreshUser = useCallback(async (): Promise<void> => {
		try {
			const fresh = await auth.me();
			setIsLoggedin(true);
			setUser(fresh);
		} catch {
			setIsLoggedin(false);
			setUser(null);
		}
	}, []);

	return isLoading ? (
		<div>Loading</div>
	) : (
		<AuthContext.Provider
			value={{
				isLoggedin,
				user,
				login,
				logout,
				signup,
				refreshUser,
				authError,
				isLoading,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export default AuthProvider;
